import React, { useState, useEffect, useCallback, useRef } from "react";
import LoadingScreen from "../common/LoadingScreen";
import ErrorScreen from "../common/ErrorScreen";
import api from "../../hooks/api";
import { API_URLS, OrderComponets } from "../../App";
import Image from "../common/Image";

interface Product {
  id: string;
  name: string;
  description: string;
  highlights: string[];
  type: string;
  image?: string;
  stock: number;
  category: string;
  rootCategory: string;
  features: Feature[];
}

interface Feature {
  name: string;
  value: string;
  order: number;
}

interface SelectedComponent {
  reference: string;
}

const ProductComponentsDetail = ({ pcConfig }: { pcConfig: Compound }) => {
  const relevantComponentsOrder = OrderComponets.map((item) => item[0]);
  const [componentDetails, setComponentDetails] = useState<
    Record<string, Product | null>
  >({}); // Allow null
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedComponent, setSelectedComponent] = useState<string | null>(
    null,
  );
  const componentListRef = useRef<HTMLUListElement>(null);

  const fetchComponentDetails = useCallback(
    async (id: string): Promise<Product | null> => {
      try {
        const response = await api.get(API_URLS.PRODUCTS + `/${id}`);
        if (response.status != 200) {
          console.warn(
            `Failed to fetch component details for ID ${id}: ${response.status} ${response.statusText}`,
          );
          return null; // Return null on failure
        }
        return response.data;
      } catch (error) {
        console.warn(`Error fetching component details for ID ${id}:`, error);
        return null; // Return null on error
      }
    },
    [],
  );
  useEffect(() => {
    const fetchAllComponents = async () => {
      setLoading(true);
      setError(null);
      try {
        const fetchedComponentDetails: Record<string, Product | null> = {};

        await Promise.all(
          Object.entries(pcConfig.selected_components ?? {}).map(
            async ([key, value]) => {
              const component = pcConfig.components[key].find(
                (c) => c.id === value?.id,
              );
              if (component?.id) {
                const details = await fetchComponentDetails(
                  component.reference,
                );
                fetchedComponentDetails[key] = details
                  ? { ...details, type: pcConfig.components[key][0].type }
                  : null;
              }
            },
          ),
        );

        const orderedComponentDetails = relevantComponentsOrder.reduce(
          (acc, key) => {
            if (fetchedComponentDetails[key])
              acc[key] = fetchedComponentDetails[key];
            return acc;
          },
          {} as Record<string, Product | null>,
        );

        Object.entries(fetchedComponentDetails).forEach(([key, value]) => {
          if (!relevantComponentsOrder.includes(key))
            orderedComponentDetails[key] = value;
        });

        setComponentDetails(orderedComponentDetails);
        setSelectedComponent(Object.keys(orderedComponentDetails)[0] || null);
      } catch (error) {
        setError(
          error instanceof Error
            ? error.message
            : "An unexpected error occurred",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchAllComponents();
  }, [pcConfig, fetchComponentDetails]);

  const handleComponentClick = (key: string) => {
    setSelectedComponent(key);
    if (componentListRef.current) {
      componentListRef.current.scrollTop = 0;
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (error) {
    return <ErrorScreen message={error} />;
  }

  return (
    <div className="components-details-view flex flex-col md:flex-row gap-4 overflow-y-auto">
      <div className="components-list w-full md:w-1/4">
        <ul ref={componentListRef} className="space-y-2 relative">
          {Object.entries(componentDetails).map(([key, component]) => (
            <li
              key={key}
              className={`component-item cursor-pointer p-2 rounded hover:text-red-500 ${selectedComponent === key ? "bg-blue-100 border-2 border-blue-500" : ""}`}
              onClick={() => handleComponentClick(key)}
            >
              <div className="component-name pb-2 font-semibold">
                {component?.type || key}
              </div>
            </li>
          ))}
        </ul>
      </div>
      {selectedComponent && componentDetails[selectedComponent] ? (
        <div
          id="component-detail"
          className="component-detail content w-full md:w-3/4 p-5 bg-white rounded-lg shadow-md"
        >
          <h3 className="component-title font-extrabold text-3xl mb-4">
            {componentDetails[selectedComponent]?.name || "Component Details"}
          </h3>
          <div className="flex flex-col md:flex-row gap-4">
            {" "}
            {/* Image and highlights side-by-side */}
            {componentDetails[selectedComponent]?.image && (
              <div className="component-image mb-4 w-full md:w-1/3">
                <Image
                  src={`https://placehold.co/500x500/B3B1A9/000000?text=${componentDetails[selectedComponent]?.name ?? "Component Image"}`}
                  dataSrc={componentDetails[selectedComponent]?.image}
                  loading="progressive"
                  alt={
                    componentDetails[selectedComponent]?.name ||
                    "Component Image"
                  }
                  sizes="500"
                  className="max-w-full h-auto rounded-md"
                />
              </div>
            )}
            <div className="component-highlights w-full md:w-2/3">
              <ul className="list-disc list-inside">
                {componentDetails[selectedComponent]?.highlights.map(
                  (highlight, index) => (
                    <li key={index}>{highlight}</li>
                  ),
                )}
              </ul>
            </div>
          </div>
          <div className="component-description mt-4">
            {componentDetails[selectedComponent]?.description ||
              "No description available."}
            {componentDetails[selectedComponent]?.features && (
              <ul className="mt-4">
                {componentDetails[selectedComponent]?.features.map(
                  (feature, index) => (
                    <li key={index}>
                      <strong className="font-semibold">{feature.name}:</strong>{" "}
                      {feature.value}
                    </li>
                  ),
                )}
              </ul>
            )}
            <p className="mt-2">
              <strong className="font-semibold">Stock:</strong>{" "}
              {componentDetails[selectedComponent]?.stock || "N/A"}
            </p>
          </div>
        </div>
      ) : (
        <div className="component-detail content w-full md:w-3/4 p-5 bg-white rounded-lg shadow-md">
          <p>Select a component to view its details.</p>
        </div>
      )}
    </div>
  );
};

export default ProductComponentsDetail;
